// src/pages/SellerProductsPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Store, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProductCard } from '@/components/products/ProductCard';
import { publicFetch } from '@/services/api';

interface SellerProduct {
  productId: number;
  productName: string;
  categoryName: string;
  subCategoryName: string;
  rating: number;
  currentPrice: number;
  originalPrice: number;
  discount: number;
  brand: string;
  primaryImageUrl: string;
}

interface SellerInfo {
  sellerId: number;
  sellerName: string;
  shopName: string;
  city: string;
  state: string;
  addressLine?: string;
  pincode?: string;
}

const SellerProductsPage = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<SellerProduct[]>([]);
  const [sellerInfo, setSellerInfo] = useState<SellerInfo | null>(null);
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  const loadProducts = async (nextPage = 0, append = false) => {
    if (!sellerId) return;

    try {
      setLoading(true);
      setError(null);

      const res = await publicFetch(
        `/api/seller/products/mobile/${sellerId}?page=${nextPage}&size=${PAGE_SIZE}`
      );
      
      if (!res || !res.ok) {
        throw new Error('Failed to load products');
      }

      const data = await res.json();

      const content = Array.isArray(data) ? data : data.content || [];
      const total = Array.isArray(data) && !(data as any).totalElements
        ? content.length
        : (data as any).totalElements ?? content.length;
      const last = Array.isArray(data) ? true : (data as any).last ?? true;

      setTotalProducts(total || 0);
      setHasMore(!last && content.length > 0);

      if (append) {
        setProducts((prev) => [...prev, ...content]);
        setPage(nextPage);
      } else {
        setProducts(content);
        setPage(nextPage);
      }
    } catch (e) {
      console.error('Seller products error:', e);
      setError('Unable to load products right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Get seller info from URL state or fetch it
    const urlParams = new URLSearchParams(window.location.search);
    const sellerName = urlParams.get('sellerName') || 'Seller';
    const city = urlParams.get('city') || '';
    const state = urlParams.get('state') || '';
    
    setSellerInfo({
      sellerId: Number(sellerId),
      sellerName,
      shopName: sellerName,
      city,
      state,
    });

    loadProducts(0, false);
  }, [sellerId]);

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    loadProducts(page + 1, true);
  };

  // Nicely formatted address line
  const locationLine = sellerInfo ? [sellerInfo.city, sellerInfo.state].filter(Boolean).join(', ') : '';
  const pinPart = sellerInfo?.pincode ? `PIN: ${sellerInfo.pincode}` : '';
  const fullAddress = [sellerInfo?.addressLine, locationLine, pinPart]
    .filter((v) => v && v.trim())
    .join(' • ');

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Seller Header */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Store className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{sellerInfo?.sellerName || 'Seller'}</h1>
                  <Badge variant="secondary">
                    {totalProducts} Products
                  </Badge>
                </div>
                <p className="text-muted-foreground mb-2">
                  Products from <span className="font-medium">{sellerInfo?.sellerName || 'this seller'}</span>
                </p>
                {fullAddress && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{fullAddress}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error message */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Products Grid */}
        {loading && products.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading products...</span>
          </div>
        ) : (
          <div className="space-y-8">
            {products.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {products.map((product, index) => (
                  <motion.div
                    key={`${product.productId}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            ) : (
              !loading && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Store className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                  <p className="text-gray-500">This seller doesn't have any products listed yet.</p>
                </div>
              )
            )}

            {/* Load More Button */}
            {hasMore && products.length > 0 && (
              <div className="text-center">
                <Button
                  onClick={handleLoadMore}
                  disabled={loading}
                  variant="outline"
                  className="min-w-[200px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading...
                    </>
                  ) : (
                    'Load More Products'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SellerProductsPage;